"use client";

import { useEffect } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Tabs, Empty, Tag, Spin } from "antd";
import {
  userGetBookCategories,
  userGetBooks,
} from "../../apiservice/userService";
import type {
  IBookCategoryData,
  IBookTypeData,
} from "../../apiservice/userService.type";
import {
  Cache_Category_Key,
  getCacheCategories,
} from "../../utils/apiSample/recommendation";
import { LockOutlined } from "@ant-design/icons";
import { BookSliderSkeleton } from "./BookSkeletonLoading";

const { TabPane } = Tabs;

const CategoryBrowser = () => {
  const [activeCategory, setActiveCategory] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [initialCategoryLoad, setInitialCategoryLoad] = useState(false);
  const [booksLoading, setBooksLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [booksError, setBooksError] = useState<string | null>(null);
  const [categories, setCategories] = useState<IBookCategoryData[]>([]);
  const [books, setBooks] = useState<IBookTypeData[]>([]);
  const [booksByCategory, setBooksByCategory] = useState<
    Record<number, IBookTypeData[]>
  >({});

  const handleCategoryChange = (categoryId: string) => {
    const newCategoryId = Number.parseInt(categoryId);
    setActiveCategory(newCategoryId);

    // Check if we already have books for this category
    if (booksByCategory[newCategoryId]) {
      setBooks(booksByCategory[newCategoryId]);
    } else {
      // Fetch books for this category
      fetchBooksByCategory(newCategoryId);
    }
  };

  const fetchBooksByCategory = async (categoryId: number) => {
    setBooksLoading(true);
    setBooksError(null);

    try {
      // Find the category title to pass to the API
      const category = categories.find((cat) => cat.id === categoryId);
      if (!category) {
        setBooksError("Category not found/ not loaded yet");
        return;
      }

      const booksData = await userGetBooks({
        categoryId: categoryId,
        page: 1,
        pageSize: 10,
      });

      if (booksData && booksData.data) {
        const fetchedBooks = booksData.data;
        setBooks(fetchedBooks);

        // Cache the books for this category
        setBooksByCategory((prev) => ({
          ...prev,
          [categoryId]: fetchedBooks,
        }));
      } else {
        setBooks([]);
        setBooksByCategory((prev) => ({
          ...prev,
          [categoryId]: [],
        }));
      }
    } catch (err) {
      setBooksError("Failed to load books for this category");
      setBooks([]);
    } finally {
      setBooksLoading(false);
    }
  };

  const fetchCategory = async () => {
    try {
      const cacheData = await getCacheCategories();
      setCategories(cacheData);

      const categoryData = await userGetBookCategories();
      if (categoryData) {
        setCategories(categoryData?.data);
        localStorage.setItem(
          Cache_Category_Key,
          JSON.stringify(categoryData?.data)
        );

        // Fetch books for the first category by default
        if (categoryData.data.length > 0) {
          const firstCategory = categoryData.data[0];
          setActiveCategory(firstCategory.id);
          setInitialCategoryLoad(true);
        }
      } else {
        setError("Category not found");
      }
    } catch (err) {
      setError("Failed to load category data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  useEffect(() => {
    if (true) {
      fetchBooksByCategory(activeCategory);
    }
  }, [initialCategoryLoad]);

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <BookSliderSkeleton title="Browse By Category" />
      </div>
    );
  }

  if (error || !categories) {
    return (
      <div className="p-4 text-center text-red-500">
        {error || "Category not found"}
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold tracking-tight mb-2 text-gray-900 dark:text-stone-100">
        Browse by Category
      </h2>

      <Tabs
        activeKey={activeCategory.toString()}
        onChange={handleCategoryChange}
        tabPosition="top"
      >
        {categories.map((category) => (
          <TabPane
            tab={
              <span className="text-gray-900 dark:text-stone-100">
                {category.title}
              </span>
            }
            key={category.id}
          >
            <div className="grid grid-cols-1 gap-3 mt-2">
              {booksLoading ? (
                <div className="text-center py-8">
                  <Spin size="large" />
                  <p className="mt-2 text-gray-700 dark:text-stone-300">
                    Loading books...
                  </p>
                </div>
              ) : booksError ? (
                <div className="text-center text-red-500 py-8">
                  {booksError}
                </div>
              ) : books.length > 0 ? (
                books.map((book) => (
                  <Link
                    key={book.id}
                    to={`/users/book-details/${book.id}/${encodeURIComponent(
                      book.title.replace(/\s+/g, "_")
                    )}`}
                    className="no-underline"
                  >
                    <div className="flex items-stretch p-3 gap-3 rounded-2xl border border-gray-100 dark:border-stone-700 bg-white dark:bg-stone-800 shadow-sm hover:shadow-md transition-shadow duration-200">
                      {/* Fixed width text section */}
                      <div className="w-[calc(100%-108px)] flex-shrink-0">
                        <h3 className="text-base font-semibold leading-snug line-clamp-2 text-gray-900 dark:text-stone-100">
                          {book.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-stone-400">
                          {book.authorName}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-stone-500 mt-2 line-clamp-2">
                          {book.summary}
                        </p>

                        <div className="mt-2 flex flex-wrap gap-1">
                          {(book.categories || []).map((cat) => (
                            <Tag key={cat.id} color="blue">
                              {cat.bookCategory?.title}
                            </Tag>
                          ))}
                          {book.isNewRelease && (
                            <Tag color="orange">New Release</Tag>
                          )}
                          {!book.hasAccess && (
                            <LockOutlined style={{ fontSize: "20px" }} />
                          )}
                        </div>
                      </div>

                      {/* Fixed width image section */}
                      <div className="w-24 h-32 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100 dark:bg-stone-700">
                        <img
                          alt={book.title}
                          src={book.coverImageUrl || "/placeholder.svg"}
                          width={96}
                          height={128}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <Empty
                  description={
                    <span className="text-gray-900 dark:text-stone-100">
                      {`No books found in ${
                        categories.find((c) => c.id === activeCategory)?.title
                      } category`}
                    </span>
                  }
                />
              )}
            </div>
          </TabPane>
        ))}
      </Tabs>
    </div>
  );
};

export default CategoryBrowser;
